import classnames from 'classnames'
import { Formik } from 'formik'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useGroup } from '../../../state/entities'
import useUpdateGroupLearningAssignment from '../../../state/mutations/useUpdateGroupLearningAssignment'
import useGroupLearningItem from '../../../state/queries/useGroupLearningItem'
import FormButtons from '../../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../../basics/TextInput/TextInput'
import GroupSlug from '../../slugs/GroupSlug/GroupSlug'
import LearningItemAvatar from '../../LearningItemAvatar/LearningItemAvatar'

import styles from './EditGroupLearningAssignmentForm.module.scss'

interface EditGroupLearningAssignmentFormProps {
  groupUuid: string
  learningItemUuid: string
  onClose: () => void
}

export default React.memo(function EditGroupLearningAssignmentForm({
  groupUuid,
  learningItemUuid,
  onClose,
}: EditGroupLearningAssignmentFormProps) {
  const { t } = useTranslation()

  // Group & learning assignment
  const { group } = useGroup(groupUuid)

  const { groupLearningItem } = useGroupLearningItem(groupUuid, learningItemUuid)

  const learningAssignment = groupLearningItem.groupLearningAssignments?.[0]

  const updateGroupLearningAssignment = useUpdateGroupLearningAssignment()

  if (!groupLearningItem.isDummy && !learningAssignment) {
    throw new Error('Learning item not found')
  }

  return (
    <article className={classnames(styles.EditGroupLearningAssignmentForm)}>
      <header>
        <hgroup>
          <h1>
            <Trans i18nKey="manage learning.edit learning assignment for group">
              <GroupSlug group={group} />
            </Trans>
          </h1>
        </hgroup>
      </header>

      <div className={styles.learningItem}>
        <LearningItemAvatar learningItem={groupLearningItem} />
        <div>{groupLearningItem.title}</div>
      </div>

      <Formik
        enableReinitialize={true}
        initialValues={{
          dueDate: learningAssignment?.dueDate,
        }}
        onSubmit={async (values) => {
          try {
            await updateGroupLearningAssignment.mutateAsync({
              uuid: learningAssignment?.uuid || '',
              dueDate: values.dueDate || undefined,
            })
            onClose()
          } catch (err) {
            console.log(err)
          }
        }}
      >
        {(props) => (
          <form noValidate onSubmit={props.handleSubmit}>
            <div>
              <TextInputFormik label={t('learning item.due date')} name="dueDate" type="date" />
            </div>

            <FormButtons onClose={onClose} {...props} />
          </form>
        )}
      </Formik>
    </article>
  )
})
